export interface BrowserDetailsSnapshot {
    userAgent: string
    platform: string
    language: string
    hardwareConcurrency: number
    deviceMemory?: number
    maxRenderableVideos?: number
    isSupportedBrowser: boolean
    isAboveMinRequirement: boolean
    browserEffectiveType?: string
    browserDownlink?: number
    browserRtt?: number
}

export interface BrowserDetailsInput {
    userAgent?: string
    platform?: string
    language?: string
    hardwareConcurrency?: number
    deviceMemory?: number
    connection?: {
        effectiveType?: string
        downlink?: number
        rtt?: number
    }
    getMaxRenderableVideos?: () => number
}

const MIN_CORE_COUNT = 2

const detectSupportedBrowser = (userAgent: string): boolean => {
    const isDesktopChrome = /^Mozilla\/5\.0 \((?:Windows|Macintosh|X11).* Chrome\//.test(userAgent)
    const isDesktopEdge = /^Mozilla\/5\.0 \((?:Windows|Macintosh|X11).* Edg\//.test(userAgent)
    return isDesktopChrome || isDesktopEdge
}

export const collectBrowserDetails = (input: BrowserDetailsInput = {}): BrowserDetailsSnapshot => {
    const userAgent = input.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '')
    const platform = input.platform ?? (typeof navigator !== 'undefined' ? navigator.platform : '')
    const language = input.language ?? (typeof navigator !== 'undefined' ? navigator.language : '')
    const hardwareConcurrency = input.hardwareConcurrency ?? (typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : 0)

    const connection = input.connection ?? (typeof navigator !== 'undefined' && 'connection' in navigator
        ? {
            effectiveType: navigator.connection?.effectiveType,
            downlink: navigator.connection?.downlink,
            rtt: navigator.connection?.rtt
        }
        : undefined)

    const deviceMemory = input.deviceMemory ?? (typeof navigator !== 'undefined' && 'deviceMemory' in navigator
        ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory
        : undefined)

    const snapshot: BrowserDetailsSnapshot = {
        userAgent,
        platform,
        language,
        hardwareConcurrency,
        isSupportedBrowser: detectSupportedBrowser(userAgent),
        isAboveMinRequirement: hardwareConcurrency > MIN_CORE_COUNT
    }

    if (deviceMemory !== undefined) {
        snapshot.deviceMemory = deviceMemory
    }

    if (input.getMaxRenderableVideos) {
        snapshot.maxRenderableVideos = input.getMaxRenderableVideos()
    }

    if (connection?.effectiveType !== undefined) {
        snapshot.browserEffectiveType = connection.effectiveType
    }

    if (connection?.downlink !== undefined) {
        snapshot.browserDownlink = connection.downlink
    }

    if (connection?.rtt !== undefined) {
        snapshot.browserRtt = connection.rtt
    }

    return snapshot
}
